import { ReactElement, useEffect, useState } from "react";
import { useSetAtom, useAtomValue } from "jotai";
import { Box, Grid, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  PrimaryButton,
  ProfileIconAndMenu,
  HelpMenu,
  NavbarPanel,
} from "@web-applications/allinga-web-components";
import NavLinks from "shared/components/NavLinks";
import {
  userPreferencesAtom,
  isConfigDialogOpenAtom,
  showHomePageAtom,
  appStatusAtom,
} from "atoms";
import { useOIDCLogin } from "shared/hooks/useOIDCLogin";
import ProfileSettingsWrapper from "shared/components/ProfileSettingsWrapper";
import IISLogo from "assets/IISLogo.svg";
import { PATHS } from "shared/constants";
import { AppStatus } from "shared/models";
import { runtimeConfig } from "shared/config/runtime-config-loader";

function Navbar(): ReactElement {
  const oidcAuth = useOIDCLogin();
  const { auth } = oidcAuth;
  const { i18n, t } = useTranslation();

  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const setConfigDialogOpen = useSetAtom(isConfigDialogOpenAtom);
  const setShowHomePage = useSetAtom(showHomePageAtom);
  const userPreferences = useAtomValue(userPreferencesAtom);
  const appStatus = useAtomValue(appStatusAtom);
  const { avatar, username, firstName, lastName } = userPreferences;
  const name =
    firstName || lastName ? `${firstName} ${lastName}` : username || "User";

  useEffect(() => {
    // default language to English if nothing found in userPreferences or if there's an error fetching userPreferences
    sessionStorage.setItem("language", userPreferences.language || "en");
    i18n.changeLanguage(userPreferences.language || "en").then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPreferences]);

  const handleLogin = async () => {
    setShowHomePage(false);
    await auth.signinRedirect();
  };

  const handleLogout = () => {
    setShowHomePage(true);
    auth.signoutRedirect();
  };

  const handleProfileSettings = () => {
    setShowProfileSettings(true);
    setConfigDialogOpen(true);
  };

  return (
    <Grid
      id="navbar"
      container
      justifyContent="space-between"
      alignItems="center"
      padding={auth.isAuthenticated ? "0" : "20px 24px"}
      sx={{
        position: "sticky",
        zIndex: 3,
        top: 0,
        backgroundColor: auth.isAuthenticated ? "#FFFFFF" : "#ebf7f7",
      }}
    >
      {!auth.isAuthenticated && (
        <Grid item>
          <Stack direction="row" gap={5} alignItems="center">
            <Box>
              <a href="/" rel="noopener noreferrer">
                <Box component="img" src={IISLogo} alt="Allinga" />
              </a>
            </Box>
          </Stack>
        </Grid>
      )}

      {appStatus === AppStatus.allowed && auth.isAuthenticated && (
        // Show menus for logged-in user
        <NavbarPanel
          leftGrid={<NavLinks />}
          rightGrid={
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ marginRight: "8px" }}
            >
              <HelpMenu
                RELEASE_NOTES_URL={PATHS.releaseNotes}
                releaseNotesText={t("helpMenu.releaseNotes")}
              />
              <ProfileIconAndMenu
                userName={name.trim()}
                avatarIcon={avatar || ""}
                profileSettingsText={t("profileSettings.title")}
                logOutText={t("profileMenu.logOut")}
                handleLogOutClick={handleLogout}
                handleProfileSettingsClick={handleProfileSettings}
                versionInfoText={t("helpMenu.versionInfo")}
                appName="Allinga Manager"
                appVersion={runtimeConfig.appVersion}
                privacyText={t("legal.privacy")}
                imprintText={t("legal.imprint")}
                THIRD_PARTY_NOTICES_URL={PATHS.thirdPartyNotices}
                thirdPartyNoticesText={t("legal.thirdPartyNotices")}
                COPYRIGHT_URL={PATHS.copyright}
                copyrightText={t("legal.copyright")}
                IMPRINT_URL="/imprint"
                PRIVACY_URL="/privacy"
                shouldOpenInNewTab={false}
                id="profile-menu"
              />
            </Stack>
          }
        />
      )}
      {!auth.isAuthenticated && (
        <PrimaryButton
          text={t("buttons.logIn")}
          onSubmit={handleLogin}
          id="navbar-login-button"
        />
      )}
      {showProfileSettings && (
        <ProfileSettingsWrapper
          handleCloseDialog={() => setShowProfileSettings(false)}
        />
      )}
    </Grid>
  );
}

export default Navbar;
